import { useLayoutEffect, useRef, useState, useSyncExternalStore } from 'react';
import {
  getNoticeStackCap,
  getNoticeStackRoomRequest,
  noticeStackRoomCap,
  publishNoticeStackGeometry,
  setNoticeSlot,
  subscribeNoticeStackCap,
  subscribeNoticeStackRoomRequest,
} from '@/lib/app-notice-stack';
import styles from './AppNoticeStack.module.css';

// Stable slot refs (module-level identities), so a re-render never detaches
// and re-attaches a slot and a mounted live region is never moved.
const warningSlot = (element: HTMLDivElement | null) => setNoticeSlot('warning', element);
const draftSlot = (element: HTMLDivElement | null) => setNoticeSlot('draft', element);
const keptSlot = (element: HTMLDivElement | null) => setNoticeSlot('kept', element);

/**
 * One fixed column for the app-level top notices (testimony plan rev 4
 * section 32.2), mounted once in App beside the gates. Slots in priority
 * order: the unsaved-recording warning, the testimony-draft notice, the
 * kept-recording notice. DOM order equals visual order, so keyboard order
 * follows what is seen. Each notice keeps its own state and runtime and only
 * portals into its slot. Presentation only.
 *
 * Height cap: the smaller of Home's cap and the keyboard room cap. The room
 * cap applies only while a sheet or dialog with its keyboard open requests
 * room (`setNoticeStackRoomRequest`, on any route) and the uncapped stack
 * leaves too little of it above the keyboard. It is derived from this stack's
 * fixed top and uncapped height, never from its displayed band, so it cannot
 * feed back; once the request is withdrawn the stack returns to its size.
 */
export default function AppNoticeStack() {
  const homeCap = useSyncExternalStore(subscribeNoticeStackCap, getNoticeStackCap, () => null);
  const room = useSyncExternalStore(subscribeNoticeStackRoomRequest, getNoticeStackRoomRequest, () => null);
  const [measuredRoomCap, setRoomCap] = useState<number | null>(null);
  // A withdrawn request removes the room cap at once (no stale measurement).
  const roomCap = room === null ? null : measuredRoomCap;
  const cap = homeCap === null ? roomCap : roomCap === null ? homeCap : Math.min(homeCap, roomCap);
  const rootRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const root = rootRef.current;
    const inner = innerRef.current;
    if (!root || !inner) return;
    let frame = 0;
    const measure = () => {
      frame = 0;
      const box = root.getBoundingClientRect();
      publishNoticeStackGeometry({
        top: box.top,
        naturalHeight: inner.offsetHeight,
        // The band the stack occupies on screen: the cap respected (the root
        // then scrolls inside itself), the content height otherwise.
        displayedHeight: box.height,
      });
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(measure);
    };
    const sizes = new ResizeObserver(schedule);
    sizes.observe(inner);
    // The root's box changes with the cap while the content does not.
    sizes.observe(root);
    const vv = window.visualViewport;
    window.addEventListener('resize', schedule);
    vv?.addEventListener('resize', schedule);
    measure();
    return () => {
      if (frame) cancelAnimationFrame(frame);
      sizes.disconnect();
      window.removeEventListener('resize', schedule);
      vv?.removeEventListener('resize', schedule);
      publishNoticeStackGeometry(null);
    };
  }, []);

  // The keyboard room cap, only while a room request is registered. It follows
  // the keyboard (the visual viewport resizes several times while it opens)
  // and the notices' own content; the stack's top is fixed.
  useLayoutEffect(() => {
    const root = rootRef.current;
    const inner = innerRef.current;
    if (room === null || !root || !inner) return;
    let frame = 0;
    const measure = () => {
      frame = 0;
      const vv = window.visualViewport;
      const visibleBottom = vv ? vv.height + vv.offsetTop : window.innerHeight;
      setRoomCap(noticeStackRoomCap(
        { top: root.getBoundingClientRect().top, naturalHeight: inner.offsetHeight },
        visibleBottom,
        room,
      ));
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(measure);
    };
    const sizes = new ResizeObserver(schedule);
    sizes.observe(inner);
    const vv = window.visualViewport;
    window.addEventListener('resize', schedule);
    vv?.addEventListener('resize', schedule);
    vv?.addEventListener('scroll', schedule);
    measure();
    return () => {
      if (frame) cancelAnimationFrame(frame);
      sizes.disconnect();
      window.removeEventListener('resize', schedule);
      vv?.removeEventListener('resize', schedule);
      vv?.removeEventListener('scroll', schedule);
    };
  }, [room]);

  return (
    <div
      ref={rootRef}
      className={styles.stack}
      style={cap !== null ? { maxHeight: `${cap}px` } : undefined}
      data-app-notice-stack=""
      data-capped={cap !== null ? '' : undefined}
    >
      <div ref={innerRef} className={styles.inner}>
        <div ref={warningSlot} className={styles.slot} data-notice-slot="warning" />
        <div ref={draftSlot} className={styles.slot} data-notice-slot="draft" />
        <div ref={keptSlot} className={styles.slot} data-notice-slot="kept" />
      </div>
    </div>
  );
}
